import { useQuery } from '@apollo/client';
import { Alert, Button, DatePicker, Spin, Tag, Tooltip } from 'antd';
import { useState } from 'react';
import { ReactComponent as CommentIcon } from '../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as LocationIcon } from '../../assets/svg/location-dot-solid.svg';
import { DATE_FORMATS, SORT } from '../../common/constants';
import { formatDate } from '../../common/utils';
import CommonTable from '../CommonTable';
import ModalComponent from '../ModalComponent';
import RenderCopyIcon from '../RenderCopyIcon';
import {
  INVENTORY_UNIT_LOGS_DEFAULT_PAGINATION,
  INVENTORY_UNIT_LOGS_DEFAULT_SORT,
  INVENTORY_UNIT_LOGS_PAGE_SIZE_OPTIONS,
  INVENTORY_UNIT_LOG_SORT_FIELDS,
  TOOLTIP_Z_INDEX,
  humanizeEnum,
} from './constants';
import {
  getInventoryUnitApis,
  hasLibraryScope,
  withLibraryScope,
} from './apiConfig';
import { LogResultTag, UnitStatusTag } from './InventoryUnitTags';
import RefreshButton from './RefreshButton';

const { RangePicker } = DatePicker;

const SORTABLE_FIELDS = Object.values(INVENTORY_UNIT_LOG_SORT_FIELDS);

const formatDateTime = (value) =>
  value ? formatDate(value, DATE_FORMATS?.WITH_TIME) : '-';

const getSortOrder = (sort, field) => {
  if (sort?.field !== field) return null;
  return sort?.order === SORT?.DESC ? 'descend' : 'ascend';
};

const buildFilter = ({
  isLibrary,
  unitId,
  libraryId,
  dateRange,
  pagination,
}) => {
  const filter = {
    unitId,
    ...withLibraryScope(isLibrary, libraryId),
    skip: (pagination?.current - 1) * pagination?.pageSize,
    limit: pagination?.pageSize,
  };
  if (dateRange?.[0] && dateRange?.[1]) {
    filter.fromDate = dateRange?.[0]?.startOf('day')?.toISOString();
    filter.toDate = dateRange?.[1]?.endOf('day')?.toISOString();
  }
  return filter;
};

const renderActor = (record) => {
  const name = [record?.actor?.firstName, record?.actor?.lastName]
    ?.filter(Boolean)
    ?.join(' ');
  const meta = [
    record?.actor?.userNumber,
    record?.actorRole && humanizeEnum(record?.actorRole),
    record?.channel && humanizeEnum(record?.channel),
  ]?.filter(Boolean);

  if (!name && !meta?.length) return '-';
  return (
    <>
      <div>{name || '-'}</div>
      {meta?.length > 0 && (
        <div className="font-size-12">{meta?.join(' · ')}</div>
      )}
    </>
  );
};

const renderPieces = (record) => {
  const hasPieces =
    record?.totalPiecesPresent !== null &&
    record?.totalPiecesPresent !== undefined;
  const pieceDiff = Number(record?.pieceDiff) || 0;

  if (!hasPieces && !pieceDiff) return '-';
  return (
    <span>
      {hasPieces ? `${record?.totalPiecesPresent} pcs` : '-'}
      {pieceDiff !== 0 && (
        <Tag color={pieceDiff > 0 ? 'success' : 'error'} className="ml-8">
          {pieceDiff > 0 ? `+${pieceDiff}` : pieceDiff}
        </Tag>
      )}
    </span>
  );
};

const InventoryUnitLogsTable = ({ isLibrary = false, unitId, libraryId }) => {
  const { logs: logsApi } = getInventoryUnitApis(isLibrary);
  const [dateRange, setDateRange] = useState(null);
  const [noteLog, setNoteLog] = useState(null);
  const [locationLog, setLocationLog] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(false);

  const handleViewLocation = (record) => {
    setIsMapLoading(true);
    setLocationLog(record);
  };
  const [sort, setSort] = useState(INVENTORY_UNIT_LOGS_DEFAULT_SORT);
  const [pagination, setPagination] = useState(
    INVENTORY_UNIT_LOGS_DEFAULT_PAGINATION,
  );

  const { data, previousData, loading, error, refetch } = useQuery(
    logsApi?.document,
    {
      variables: {
        filter: buildFilter({
          isLibrary,
          unitId,
          libraryId,
          dateRange,
          pagination,
        }),
        sort,
      },
      skip: !unitId || !hasLibraryScope(isLibrary, libraryId),
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError() {},
    },
  );

  const response = (data || previousData)?.[logsApi?.key];
  const logs = response?.data || [];
  const totalCount = response?.count || 0;

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (tablePagination, _tableFilter, sorter) => {
    const isPageSizeChanged =
      tablePagination?.pageSize !== pagination?.pageSize;
    setPagination({
      current: isPageSizeChanged ? 1 : tablePagination?.current,
      pageSize: tablePagination?.pageSize,
    });
    if (sorter?.order && SORTABLE_FIELDS?.includes(sorter?.field)) {
      setSort({
        field: sorter?.field,
        order: sorter?.order === 'descend' ? SORT?.DESC : SORT?.ASC,
      });
    } else {
      setSort(INVENTORY_UNIT_LOGS_DEFAULT_SORT);
    }
  };

  const columns = [
    {
      title: 'SEQ NO',
      dataIndex: INVENTORY_UNIT_LOG_SORT_FIELDS?.SEQUENCE_NO,
      key: INVENTORY_UNIT_LOG_SORT_FIELDS?.SEQUENCE_NO,
      width: 110,
      fixed: 'left',
      sorter: true,
      sortOrder: getSortOrder(
        sort,
        INVENTORY_UNIT_LOG_SORT_FIELDS?.SEQUENCE_NO,
      ),
      render: (sequenceNo) => sequenceNo ?? '-',
    },
    {
      title: 'EVENT',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 170,
      fixed: 'left',
      render: (eventType) =>
        eventType ? <Tag color="blue">{humanizeEnum(eventType)}</Tag> : '-',
    },
    {
      title: 'FROM STATUS',
      dataIndex: 'fromStatus',
      key: 'fromStatus',
      width: 180,
      render: (fromStatus) => <UnitStatusTag status={fromStatus} />,
    },
    {
      title: 'TO STATUS',
      dataIndex: 'toStatus',
      key: 'toStatus',
      width: 180,
      render: (toStatus) => <UnitStatusTag status={toStatus} />,
    },
    {
      title: 'RESULT',
      dataIndex: 'result',
      key: 'result',
      width: 120,
      render: (result) => <LogResultTag result={result} />,
    },
    {
      title: 'PIECES',
      dataIndex: 'totalPiecesPresent',
      key: 'totalPiecesPresent',
      width: 190,
      render: (_, record) => renderPieces(record),
    },
    {
      title: 'CREATED BY',
      dataIndex: 'actor',
      key: 'actor',
      width: 240,
      render: (_, record) => renderActor(record),
    },
    {
      title: 'NOTES',
      dataIndex: 'notes',
      key: 'notes',
      width: 110,
      align: 'left',
      render: (_, record) =>
        record?.notes ||
        record?.blockReasonMessage ||
        record?.overrideReason ? (
          <Tooltip title="View note" placement="top" zIndex={TOOLTIP_Z_INDEX}>
            <CommentIcon
              className="pointer svg-icon"
              onClick={() => setNoteLog(record)}
            />
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'MAPPED ORDER NUMBER',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 220,
      render: (_, record) => {
        const orderNumber =
          record?.metadata?.returnOrderNumber || record?.metadata?.orderNumber;
        if (!orderNumber) return '-';
        return (
          <div className="d-flex align-center">
            <RenderCopyIcon
              copyText={orderNumber}
              toolTipText="Copy Order Number"
              zIndex={TOOLTIP_Z_INDEX}
            />
            <span>{orderNumber}</span>
          </div>
        );
      },
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      width: 120,
      render: (_, record) =>
        record?.latitude && record?.longitude ? (
          <Tooltip title="View in map" placement="top" zIndex={TOOLTIP_Z_INDEX}>
            <span
              role="button"
              tabIndex={0}
              aria-label="View in map"
              className="pointer"
              onClick={() => handleViewLocation(record)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  e?.preventDefault();
                  handleViewLocation(record);
                }
              }}
            >
              <LocationIcon className="svg-icon" />
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'CREATED AT',
      dataIndex: INVENTORY_UNIT_LOG_SORT_FIELDS?.CREATED_AT,
      key: INVENTORY_UNIT_LOG_SORT_FIELDS?.CREATED_AT,
      width: 190,
      sorter: true,
      sortOrder: getSortOrder(sort, INVENTORY_UNIT_LOG_SORT_FIELDS?.CREATED_AT),
      render: (createdAt) => formatDateTime(createdAt),
    },
  ];

  return (
    <div className="inventory-unit-logs">
      <div className="inventory-unit-logs-header">
        <span className="inventory-unit-logs-title">
          Unit History ({totalCount})
        </span>
        <div className="inventory-unit-logs-actions">
          <RangePicker
            allowClear
            value={dateRange}
            format={DATE_FORMATS?.REGULAR}
            onChange={handleDateRangeChange}
          />
          <RefreshButton
            title="Refresh history"
            loading={loading}
            onRefresh={refetch}
          />
        </div>
      </div>
      {error && !loading ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load unit history"
          description={error?.message}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <CommonTable
          columns={columns}
          data={logs}
          loadingData={loading}
          onChange={handleTableChange}
          fullHeight={false}
          scroll={{ x: 'max-content' }}
          size="small"
          rowKey={(record) => record?.id}
          locale={{ emptyText: 'No history found' }}
          paginationConfig={{
            current: pagination?.current,
            pageSize: pagination?.pageSize,
            total: totalCount,
            size: 'small',
            pageSizeOptions: INVENTORY_UNIT_LOGS_PAGE_SIZE_OPTIONS,
          }}
        />
      )}

      <ModalComponent
        open={Boolean(noteLog)}
        onCancel={() => setNoteLog(null)}
        footer={null}
      >
        {noteLog?.notes && (
          <div className="mb-8">
            <b className="font-size-16">Note</b>
            <div className="font-size-16">{noteLog?.notes}</div>
          </div>
        )}
        {noteLog?.blockReasonMessage && (
          <div className="mb-8">
            <b className="font-size-16">Block Reason</b>
            <div className="font-size-16">{noteLog?.blockReasonMessage}</div>
          </div>
        )}
        {noteLog?.overrideReason && (
          <div>
            <b className="font-size-16">Override Reason</b>
            <div className="font-size-16">{noteLog?.overrideReason}</div>
          </div>
        )}
      </ModalComponent>

      <ModalComponent
        title="Location"
        wrapClassName="inventory-unit-location-modal"
        open={Boolean(locationLog)}
        onCancel={() => setLocationLog(null)}
        footer={null}
        width={800}
        maskClosable
        destroyOnClose
      >
        <div className="inventory-unit-location-map">
          {isMapLoading && (
            <div className="inventory-unit-location-map-loader">
              <Spin />
              <span>Loading map...</span>
            </div>
          )}
          <iframe
            title="Unit history location"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://maps.google.com/maps?q=${locationLog?.latitude},${locationLog?.longitude}&z=15&output=embed`}
            onLoad={() => setIsMapLoading(false)}
          />
        </div>
      </ModalComponent>
    </div>
  );
};

export default InventoryUnitLogsTable;
